import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Video } from '../../types';
import { db } from '../../firebase';
import { doc, getDoc, addDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { useToast } from '../../hooks/useToast';

const extractYouTubeID = (urlOrId: string): string | null => {
    if (!urlOrId) return null;
    // Check if it's just the ID
    const idRegex = /^[a-zA-Z0-9_-]{11}$/;
    if (idRegex.test(urlOrId)) {
        return urlOrId;
    }
    // Check for standard YouTube URL formats
    const urlRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = urlOrId.match(urlRegex);
    return match ? match[1] : null;
};

const AdminVideoFormPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id);
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    
    const [video, setVideo] = useState<Omit<Video, 'id'>>({
        title: '',
        youtubeVideoId: '',
        description: '',
        order: 0,
        isHero: false,
    });

    useEffect(() => {
        const fetchData = async () => {
            if (isEditing && id) {
                const docRef = doc(db, 'videos', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setVideo(docSnap.data() as Video);
                } else {
                    showToast('Video not found.', { type: 'error' });
                    navigate('/admin/videos');
                }
            } else {
                const snapshot = await getDocs(collection(db, 'videos'));
                setVideo(prev => ({ ...prev, order: snapshot.size }));
            }
            setIsLoading(false);
        };
        fetchData();
    }, [id, isEditing, navigate, showToast]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            setVideo(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
        } else {
            setVideo(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const extractedId = extractYouTubeID(e.target.value);
        setVideo(prev => ({ ...prev, youtubeVideoId: extractedId || e.target.value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const finalId = extractYouTubeID(video.youtubeVideoId);
        if (!finalId) {
            showToast('Invalid YouTube URL or Video ID.', { type: 'error' });
            return;
        }

        setIsSaving(true);
        try {
            const dataToSave = { ...video, youtubeVideoId: finalId };
            if (isEditing && id) {
                await updateDoc(doc(db, 'videos', id), dataToSave);
                showToast('Video updated successfully!', { type: 'success' });
            } else {
                await addDoc(collection(db, 'videos'), dataToSave);
                showToast('Video added successfully!', { type: 'success' });
            }
            navigate('/admin/videos');
        } catch (error) {
            showToast('Failed to save video.', { type: 'error' });
            setIsSaving(false);
        }
    };

    if (isLoading) return <div className="text-center p-8">Loading...</div>;
    
    const thumbnailUrl = video.youtubeVideoId ? `https://i.ytimg.com/vi/${video.youtubeVideoId}/hqdefault.jpg` : '';

    return (
        <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">{isEditing ? 'Edit Video' : 'Add New Video'}</h2>
            <form onSubmit={handleSubmit}>
                <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl mx-auto space-y-6">
                    <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700">Video Title</label>
                        <input type="text" name="title" id="title" value={video.title} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                    </div>
                    <div>
                        <label htmlFor="youtubeVideoId" className="block text-sm font-medium text-gray-700">YouTube URL or Video ID</label>
                        <input type="text" name="youtubeVideoId" id="youtubeVideoId" value={video.youtubeVideoId} onBlur={handleIdChange} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                        <p className="mt-1 text-xs text-gray-500">e.g., https://www.youtube.com/watch?v=dQw4w9WgXcQ or just dQw4w9WgXcQ</p>
                    </div>
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea name="description" id="description" rows={3} value={video.description} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"></textarea>
                    </div>

                    <div className="flex items-start">
                        <div className="flex items-center h-5">
                            <input id="isHero" name="isHero" type="checkbox" checked={video.isHero || false} onChange={handleChange} className="h-4 w-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500" />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="isHero" className="font-medium text-gray-700">Set as Homepage Hero</label>
                            <p className="text-gray-500">If checked, this video will be used as the background for the homepage hero section, replacing the project slider.</p>
                        </div>
                    </div>

                    {thumbnailUrl && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Thumbnail Preview</label>
                            <img src={thumbnailUrl} alt="YouTube thumbnail preview" className="mt-1 w-48 h-auto border rounded-md" />
                        </div>
                    )}
                    <div className="flex justify-end space-x-4 pt-4">
                        <button type="button" onClick={() => navigate('/admin/videos')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                            Cancel
                        </button>
                        <button type="submit" disabled={isSaving} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400">
                            {isSaving ? 'Saving...' : 'Save Video'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AdminVideoFormPage;